import React, { useCallback, useEffect, useReducer, useState } from "react";

import openSocket from "socket.io-client";

import {
  Avatar,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";

import { makeStyles } from "tss-react/mui";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import {
  DeleteOutlined,
  SmartToyOutlined,
  SubdirectoryArrowRightOutlined,
} from "@mui/icons-material";
import PromptModal from "../../components/PromptModal";
import { toast } from "../../services/toast";
import ConfirmationModal from "../../components/ConfirmationModal";

const CHANNEL_TABS = ["text", "voice"];
const NEW_PROMPT = "new";

const useStyles = makeStyles()((theme) => ({
  layout: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 2),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  listPanel: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    cursor: "pointer",
    minWidth: 0,
    "&:hover": {
      background: theme.palette.action.hover,
    },
    "&:hover .delete-button": {
      visibility: "visible",
    },
  },
  itemSelected: {
    background: theme.palette.action.selected,
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    "&:hover": {
      background: theme.palette.action.selected,
    },
  },
  childItem: {
    paddingLeft: theme.spacing(2.5),
  },
  avatar: {
    width: 34,
    height: 34,
    backgroundColor: theme.palette.mode === "light" ? "#eef0ff" : "rgba(255,255,255,0.08)",
    color: "#5b6cff",
    flexShrink: 0,
    "& svg": { fontSize: 19 },
  },
  childAvatar: {
    width: 26,
    height: 26,
    backgroundColor: theme.palette.mode === "light" ? "#f2f3f7" : "rgba(255,255,255,0.05)",
    color: theme.palette.text.secondary,
    flexShrink: 0,
    "& svg": { fontSize: 15 },
  },
  childArrow: {
    color: theme.palette.text.disabled,
    fontSize: 16,
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  dimmed: {
    opacity: 0.45,
  },
  statusChip: {
    flexShrink: 0,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "2px 7px",
    borderRadius: 999,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
  },
  statusDot: {
    position: "absolute",
    right: -1,
    bottom: -1,
    width: 9,
    height: 9,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
  },
  dotOn: {
    background: theme.palette.success.main,
  },
  dotOff: {
    background: theme.palette.text.disabled,
  },
  avatarWrap: {
    position: "relative",
    display: "flex",
    flexShrink: 0,
  },
  itemName: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
  },
  childName: {
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.3,
  },
  itemMeta: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    visibility: "hidden",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.5, 1, 0.5),
  },
  empty: {
    padding: theme.spacing(3, 1),
    textAlign: "center",
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  editorPanel: {
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  editorEmpty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  usageWarning: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.warning.main}`,
    fontSize: 13,
    lineHeight: 1.6,
  },
  usageList: {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(2.5),
  },
  usageDanger: {
    color: theme.palette.error.main,
  },
  usageMinor: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12.5,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    return action.payload;
  }

  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const promptIndex = state.findIndex((p) => p.id === prompt.id);
    if (promptIndex !== -1) {
      const next = [...state];
      next[promptIndex] = { ...next[promptIndex], ...prompt };
      return next;
    }
    return [prompt, ...state];
  }

  if (action.type === "DELETE_PROMPT") {
    return state.filter((p) => p.id !== action.payload);
  }

  return state;
};

const PromptUsageWarning = ({ usage, classes }) => {
  if (!usage) return null;

  const rompe = [
    usage.connections?.length && `conexiones: ${usage.connections.join(", ")}`,
    usage.queues?.length && `departamentos: ${usage.queues.join(", ")}`,
    usage.children?.length && `pierden este especialista: ${usage.children.join(", ")}`,
  ].filter(Boolean);

  if (!rompe.length && !usage.tickets) return null;

  return (
    <div className={classes.usageWarning}>
      {rompe.length > 0 && (
        <>
          <strong>Esto va a quedar sin prompt:</strong>
          <ul className={classes.usageList}>
            {rompe.map((linea) => (
              <li key={linea}>{linea}</li>
            ))}
          </ul>
          {usage.connections?.length > 0 && (
            <span className={classes.usageDanger}>
              Una conexión sin prompt atiende la llamada sin saber qué decir.
            </span>
          )}
        </>
      )}
      {usage.tickets > 0 && (
        <div className={classes.usageMinor}>
          Además, {usage.tickets} ticket{usage.tickets === 1 ? "" : "s"} lo mencionan. Es
          historial: se desvinculan y no pasa nada.
        </div>
      )}
    </div>
  );
};

const AgentItem = ({ prompt, nested, selected, meta, classes, onSelect, onDelete }) => {
  const isSpecialist = prompt.type === "specialist";
  const off = isSpecialist && prompt.active === false;
  return (
  <div
    className={`${classes.item} ${nested ? classes.childItem : ""} ${selected ? classes.itemSelected : ""}`}
    onClick={() => onSelect(prompt.id)}
  >
    {nested && <SubdirectoryArrowRightOutlined className={classes.childArrow} />}
    <span className={`${classes.avatarWrap} ${off ? classes.dimmed : ""}`}>
      <Avatar className={nested ? classes.childAvatar : classes.avatar}>
        <SmartToyOutlined />
      </Avatar>
      {isSpecialist && <span className={`${classes.statusDot} ${off ? classes.dotOff : classes.dotOn}`} />}
    </span>
    <div className={`${classes.itemText} ${off ? classes.dimmed : ""}`}>
      <Typography className={nested ? classes.childName : classes.itemName} noWrap>
        {prompt.name}
      </Typography>
      {meta && (
        <Typography className={classes.itemMeta} noWrap>
          {meta}
        </Typography>
      )}
    </div>
    {off && <span className={classes.statusChip}>{i18n.t("prompts.inactive")}</span>}
    <Tooltip title={i18n.t("prompts.delete")}>
      <IconButton
        size="small"
        className={`${classes.deleteButton} delete-button`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(prompt);
        }}
      >
        <DeleteOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  </div>
  );
};

const specialistMeta = (prompt) => {
  const tools = prompt.herramientasWebhook?.length || 0;
  const detail = tools
    ? `${tools} ${i18n.t(tools === 1 ? "prompts.card.herramienta" : "prompts.card.herramientas")}`
    : i18n.t("prompts.types.specialist");
  return detail;
};

const parentMeta = (prompt, channel) => {
  const specialists = prompt.promptsHijos?.length || 0;
  const parts = [];
  if (channel === "voice" && prompt.realtimeModel) parts.push(prompt.realtimeModel);
  if (specialists) {
    parts.push(
      `${specialists} ${i18n.t(specialists === 1 ? "prompts.card.especialista" : "prompts.card.especialistas")}`
    );
  }
  return parts.join(" · ");
};

const Prompts = () => {
  const { classes } = useStyles();

  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("text");
  const [selectedId, setSelectedId] = useState(null);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [promptUsage, setPromptUsage] = useState(null);

  const promptsById = new Map(prompts.map((prompt) => [prompt.id, prompt]));
  const channelPrompts = prompts.filter((prompt) => (prompt.type || "text") === channel);
  const unlinkedSpecialists = prompts.filter(
    (prompt) => prompt.type === "specialist" && !(prompt.parentPrompts || []).length
  );
  const countFor = (type) => prompts.filter((prompt) => (prompt.type || "text") === type).length;

  const loadPrompts = useCallback(async () => {
    try {
      const { data } = await api.get("/prompt");
      dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  useEffect(() => {
    const socket = openSocket(process.env.REACT_APP_BACKEND_URL);

    socket.on("prompt", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (loading || selectedId === NEW_PROMPT) return;
    if (selectedId && promptsById.has(selectedId)) return;
    setSelectedId(channelPrompts[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, prompts, channel]);

  const changeChannel = (value) => {
    setChannel(value);
    const first = prompts.find((prompt) => (prompt.type || "text") === value);
    setSelectedId(first?.id ?? null);
  };

  const askDelete = async (prompt) => {
    setPromptToDelete(prompt);
    setPromptUsage(null);
    setConfirmModalOpen(true);
    try {
      const { data } = await api.get(`/prompt/${prompt.id}/usage`);
      setPromptUsage(data);
    } catch (err) {
      setPromptUsage(null);
    }
  };

  const closeConfirmation = () => {
    setConfirmModalOpen(false);
    setPromptToDelete(null);
    setPromptUsage(null);
  };

  const deletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
      if (selectedId === promptId) setSelectedId(null);
      await loadPrompts();
    } catch (err) {
      toastError(err);
    }
    setPromptToDelete(null);
  };

  const handleSaved = async (savedId) => {
    await loadPrompts();
    if (savedId) setSelectedId(savedId);
  };

  const selectedPrompt = selectedId && selectedId !== NEW_PROMPT ? promptsById.get(selectedId) : null;

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={promptToDelete && `${i18n.t("prompts.confirmationModal.deleteTitle")} ${promptToDelete.name}?`}
        open={confirmModalOpen}
        onClose={closeConfirmation}
        onConfirm={() => deletePrompt(promptToDelete.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
        <PromptUsageWarning usage={promptUsage} classes={classes} />
      </ConfirmationModal>

      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={() => setSelectedId(NEW_PROMPT)}>
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.layout}>
        <Paper variant="outlined" className={classes.listPanel}>
          <Tabs value={channel} onChange={(_, value) => changeChannel(value)} variant="fullWidth" className={classes.tabs}>
            {CHANNEL_TABS.map((type) => (
              <Tab key={type} value={type} label={`${i18n.t(`prompts.tabs.${type}`)} (${countFor(type)})`} />
            ))}
          </Tabs>

          <div className={classes.list}>
            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={44} style={{ margin: 6 }} />
              ))}

            {!loading && channelPrompts.length === 0 && (
              <Typography className={classes.empty}>{i18n.t(`prompts.emptyTab.${channel}`)}</Typography>
            )}

            {channelPrompts.map((prompt) => (
              <React.Fragment key={prompt.id}>
                <AgentItem
                  prompt={prompt}
                  meta={parentMeta(prompt, channel)}
                  selected={selectedId === prompt.id}
                  classes={classes}
                  onSelect={setSelectedId}
                  onDelete={askDelete}
                />
                {(prompt.promptsHijos || []).map((child) => {
                  const specialist = promptsById.get(child.id) || child;
                  return (
                    <AgentItem
                      key={`${prompt.id}-${child.id}`}
                      prompt={specialist}
                      nested
                      meta={specialistMeta(specialist)}
                      selected={selectedId === child.id}
                      classes={classes}
                      onSelect={setSelectedId}
                      onDelete={askDelete}
                    />
                  );
                })}
              </React.Fragment>
            ))}

            {unlinkedSpecialists.length > 0 && (
              <Typography className={classes.sectionLabel}>{i18n.t("prompts.unlinkedSpecialists")}</Typography>
            )}
            {unlinkedSpecialists.map((specialist) => (
              <AgentItem
                key={`unlinked-${specialist.id}`}
                prompt={specialist}
                meta={specialistMeta(specialist)}
                selected={selectedId === specialist.id}
                classes={classes}
                onSelect={setSelectedId}
                onDelete={askDelete}
              />
            ))}
          </div>
        </Paper>

        <div className={classes.editorPanel}>
          {selectedId === NEW_PROMPT || selectedPrompt ? (
            <PromptModal
              key={selectedId}
              inline
              open
              promptId={selectedPrompt?.id}
              defaultType={channel}
              onSaved={handleSaved}
            />
          ) : (
            <Paper variant="outlined" className={classes.editorEmpty}>
              {i18n.t("prompts.selectHint")}
            </Paper>
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default Prompts;
